import React, { useState } from "react";
import { DatePicker, Space, Select } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { getSelectOptions } from "@shared/lib/constants";
import { ColumnFilterWrapper } from "./ColumnFilterWrapper";

const { RangePicker } = DatePicker;

interface DateRange {
  from?: string;
  to?: string;
}

interface DateRangeFilterProps {
  props: { confirm: () => void; close: () => void };
  title?: string;
  placeholder?: string;
  selectedRange?: DateRange;
  selectedStageInitial?: string[];
  selectedCreatedByTypeInitial?: string[];
  handlePropsChange: (props: {
    range?: DateRange;
    stage?: string[];
    createdByType?: string[];
  }) => void;
  type: "discovery" | "pricing" | "all";
  filterCount?: number;
}

export default function InfluencerCommercialFilter({
  props,
  title = "Commercials",
  placeholder,
  selectedRange,
  handlePropsChange,
  selectedStageInitial,
  type = "all",
  selectedCreatedByTypeInitial,
  filterCount,
}: DateRangeFilterProps) {
  const [value, setValue] = useState<[Dayjs | null, Dayjs | null]>([
    selectedRange?.from ? dayjs(selectedRange.from) : null,
    selectedRange?.to ? dayjs(selectedRange.to) : null,
  ]);

  const [selectedStage, setSelectedStage] = useState<string[]>(
    selectedStageInitial || []
  );
  const [selectedCreatedByType, setSelectedCreatedByType] = useState<string[]>(
    selectedCreatedByTypeInitial || []
  );

  const selectOptions = getSelectOptions(type);

  const onChange = (dates: any) => {
    let safeDates: [Dayjs | null, Dayjs | null] = [null, null];
    if (Array.isArray(dates) && dates.length === 2) {
      safeDates = [dates[0], dates[1]];
    }
    setValue(safeDates);
  };

  const isFiltered = !!(
    selectedRange?.from ||
    selectedRange?.to ||
    selectedStage.length > 0 ||
    selectedCreatedByType.length > 0
  );

  const calculatedCount =
    (selectedRange?.from || selectedRange?.to ? 1 : 0) +
    selectedStage.length +
    selectedCreatedByType.length;

  const handleApply = () => {
    handlePropsChange({
      range: {
        from: value[0]?.toISOString(),
        to: value[1] ? value[1].endOf("day").toISOString() : undefined,
      },
      stage: selectedStage,
      createdByType: selectedCreatedByType,
    });
    props.confirm();
  };

  const handleResetInternal = () => {
    setValue([null, null]);
    setSelectedStage([]);
    setSelectedCreatedByType([]);
    handlePropsChange({
      range: undefined,
      stage: [],
      createdByType: [],
    });
    props.confirm();
  };

  return (
    <ColumnFilterWrapper
      title={title}
      filterCount={filterCount ?? calculatedCount}
      isFiltered={isFiltered}
      confirm={props.confirm}
      close={props.close}
      onApply={handleApply}
      onReset={handleResetInternal}
    >
      <Space direction="vertical" style={{ width: "100%" }} size={12}>
        <div>
          <p style={{ margin: "0 0 4px 0", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#9ba3b2" }}>
            Date
          </p>
          <RangePicker
            value={value}
            onChange={onChange}
            style={{ width: "100%" }}
            allowEmpty
            placeholder={
              placeholder
                ? ([`${placeholder} From`, `${placeholder} To`] as [
                    string,
                    string
                  ])
                : undefined
            }
          />
        </div>
        <div>
          <p style={{ margin: "0 0 4px 0", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#9ba3b2" }}>
            Stage
          </p>
          <Select
            options={selectOptions}
            mode="multiple"
            value={selectedStage}
            onChange={setSelectedStage}
            style={{ width: "100%" }}
            placeholder="Select Stage"
          />
        </div>
        <div>
          <p style={{ margin: "0 0 4px 0", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#9ba3b2" }}>
            Cost Added By
          </p>
          <Select
            options={[
              { label: "Admin", value: "admin-user" },
              { label: "Influencer", value: "brand-user" },
              { label: "Brand", value: "influencer-user" },
              { label: "Agency", value: "agency-user" },
            ]}
            mode="multiple"
            value={selectedCreatedByType}
            onChange={setSelectedCreatedByType}
            style={{ width: "100%" }}
            placeholder="Select User Type"
          />
        </div>
      </Space>
    </ColumnFilterWrapper>
  );
}
